<template>
  <div
    class="carrossel-step-banners"
    :class="{ 'carrossel-step-banners--dark': darkEnabled }"
  >
    <h3 class="step-title">Organize seus banners</h3>
    <p class="step-subtitle">
      Arraste para definir a ordem de exibição dos banners no carrossel
    </p>

    <v-divider class="mb-5" />

    <v-row>
      <v-col cols="12" lg="8">
        <div class="step-column">
          <Draggable
            v-model="slides"
            class="banners-row"
            draggable=".banner-card"
            :tag="'div'"
          >
            <div
              v-for="(slide, index) in slides"
              :key="slide.id"
              class="banner-card"
              :class="{ 'banner-card--selected': slide.id === selectedSlideId }"
              @click="selectSlide(slide.id)"
            >
              <div class="banner-card__handle">
                <v-icon small>fas fa-grip-vertical</v-icon>
              </div>
              <div class="banner-card__index">{{ index + 1 }}</div>
              <img
                :src="getSlideImageUrl(slide, wizardState.slideImageRevisions)"
                alt=""
                class="banner-card__image"
              />
              <div class="banner-card__actions">
                <FormButton
                  class="banner-btn-replace"
                  text="Substituir imagem"
                  outlined
                  before-icon="fas fa-upload"
                  @click.stop="triggerReplace(slide.id)"
                />
                <FormButton
                  class="banner-btn-remove"
                  text="Remover"
                  outlined
                  type="perigo"
                  before-icon="fas fa-trash"
                  @click.stop="removeSlide(slide.id)"
                />
              </div>
            </div>

            <div class="banner-card banner-card--upload">
              <DragDropUploader
                initial-icon-class="fas fa-cloud-upload-alt"
                :upload-multiple="true"
                :upload-url="uploadUrl"
                @upload-success="onUploadSuccess"
              />
              <small class="upload-hint"
                >PNG, JPG, WEBP ou GIF · máx. 5 MB</small
              >
            </div>
          </Draggable>
        </div>
      </v-col>

      <v-col cols="12" lg="4">
        <div
          class="step-column step-column--side"
          :class="{ 'step-column--empty': !selectedSlide }"
        >
          <template v-if="selectedSlide">
            <h4 class="side-panel-title">
              Configurações do banner {{ selectedSlideIndex + 1 }}
            </h4>
            <CarrosselImagemTemplate
              ref="slideActionPanel"
              :key="selectedSlide.id"
              panel-mode
              :slide="selectedSlide"
              :setores-folha="setoresFolha"
              :paginas-estaticas="paginasEstaticas"
              :paginas-navegacao="paginasNavegacao"
              :show-componente="showComponente"
              :refresh-slide-action="refreshSlideAction"
              :delete-slide="() => {}"
            />
            <CarrosselBannerProgramacao
              :key="`programacao-${selectedSlide.id}`"
              :slide="selectedSlide"
              :inicio-vigencia="wizardState.carrossel.inicio_vigencia"
              :fim-vigencia="wizardState.carrossel.fim_vigencia"
              @dirty="$emit('dirty')"
            />
          </template>
          <p v-else class="side-panel-empty">
            Selecione um banner para editar as configurações do banner.
          </p>
        </div>
      </v-col>
    </v-row>

    <input
      ref="replaceInput"
      type="file"
      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
      class="hidden-input"
      @change="onReplaceFile"
    />
  </div>
</template>

<script>
import Draggable from "vuedraggable";
import FormButton from "@/components/form/FormButton";
import DragDropUploader from "@/components/Drag-drop-uploader";
import CarrosselImagemTemplate from "../template/CarrosselImagemTemplate.vue";
import CarrosselBannerProgramacao from "../template/CarrosselBannerProgramacao.vue";
import {
  CARROSSEL_UPDATE_IMAGE,
  CARROSSEL_DELETE_SLIDE
} from "@/store/actions/carrossel";
import {
  getSlideImageUrl,
  validateStepBanners,
  mergeSlidesAfterServerChange,
  cloneWizardSnapshot,
  restoreWizardFromSnapshot,
  BANNER_IRREVERSIBLE_MESSAGES,
  BANNER_REPLACE_SAVE_MESSAGE
} from "../../utils/utilCarrossel";
import { mapGetters } from "vuex";

export default {
  name: "CarrosselStepBanners",
  components: {
    Draggable,
    FormButton,
    DragDropUploader,
    CarrosselImagemTemplate,
    CarrosselBannerProgramacao
  },
  props: {
    wizardState: {
      type: Object,
      required: true
    },
    setoresFolha: {
      type: Array,
      default: () => []
    },
    paginasEstaticas: {
      type: Array,
      default: () => []
    },
    paginasNavegacao: {
      type: Array,
      default: () => []
    },
    showComponente: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      selectedSlideId: null,
      replaceSlideId: null,
      stepBaseline: null,
      removingSlideId: null
    };
  },
  mounted() {
    this.captureStepBaseline();
  },
  computed: {
    ...mapGetters(["getFornecedorId", "darkEnabled"]),
    slides: {
      get() {
        return this.wizardState.carrossel?.slides || [];
      },
      set(value) {
        this.wizardState.carrossel.slides = value;
        this.$emit("dirty");
      }
    },
    selectedSlide() {
      return this.slides.find(slide => slide.id === this.selectedSlideId);
    },
    selectedSlideIndex() {
      return this.slides.findIndex(slide => slide.id === this.selectedSlideId);
    },
    uploadUrl() {
      const idcarrossel = this.wizardState.carrossel?.idcarrossel;
      if (!idcarrossel) return "";
      return `/api/v4/carrossel/upload-image/${this.getFornecedorId}/${idcarrossel}`;
    }
  },
  watch: {
    slides: {
      immediate: true,
      handler(list) {
        if (list?.length && !this.selectedSlideId) {
          this.selectedSlideId = list[0].id;
        }
        if (
          this.selectedSlideId &&
          !list.find(s => s.id === this.selectedSlideId)
        ) {
          this.selectedSlideId = list[0]?.id || null;
        }
      }
    }
  },
  methods: {
    getSlideImageUrl,
    selectSlide(id) {
      this.selectedSlideId = id;
    },
    onUploadSuccess(event) {
      const carrossel = event?.response || event;
      if (carrossel?.slides) {
        const previousIds = new Set(
          (this.slides || []).map(slide => slide?.id).filter(Boolean)
        );
        const mergedSlides = mergeSlidesAfterServerChange(
          this.slides,
          carrossel.slides
        );
        const addedSlideIds = mergedSlides
          .filter(slide => slide?.id && !previousIds.has(slide.id))
          .map(slide => slide.id);

        this.wizardState.carrossel.slides = mergedSlides;
        if (addedSlideIds.length) {
          this.$emit("slides-added", addedSlideIds);
        }
        this.$emit("dirty");
      }
    },
    async confirmIrreversibleBannerAction(actionType) {
      const message = BANNER_IRREVERSIBLE_MESSAGES[actionType];
      if (!message) return false;

      const result = await this.$swal.fire({
        title: message.title,
        text: message.text,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirmar",
        cancelButtonText: "Cancelar",
        reverseButtons: true
      });

      return Boolean(result.value);
    },
    async confirmReplaceBannerAction() {
      const result = await this.$swal.fire({
        title: BANNER_REPLACE_SAVE_MESSAGE.title,
        text: BANNER_REPLACE_SAVE_MESSAGE.text,
        type: "info",
        showCancelButton: true,
        confirmButtonText: "Continuar",
        cancelButtonText: "Cancelar",
        reverseButtons: true
      });

      return Boolean(result.value);
    },
    async triggerReplace(slideId) {
      const confirmed = await this.confirmReplaceBannerAction();
      if (!confirmed) return;

      this.replaceSlideId = slideId;
      this.$refs.replaceInput.click();
    },
    async onReplaceFile(event) {
      const file = event.target.files?.[0];
      event.target.value = "";

      if (!file || !this.replaceSlideId) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        const carrossel = await this.$store.dispatch(CARROSSEL_UPDATE_IMAGE, {
          idcarrossel: this.wizardState.carrossel.idcarrossel,
          idslide: this.replaceSlideId,
          formData
        });
        this.$set(this.wizardState, "slideImageRevisions", {
          ...(this.wizardState.slideImageRevisions || {}),
          [this.replaceSlideId]: Date.now()
        });
        this.wizardState.carrossel.slides = mergeSlidesAfterServerChange(
          this.slides,
          carrossel.slides || []
        );
        this.$emit("dirty");
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Erro ao substituir imagem";
        this.$vueOnToast.pop("error", message);
      } finally {
        this.replaceSlideId = null;
      }
    },
    async removeSlide(slideId) {
      if (this.removingSlideId) return;

      const idcarrossel = this.wizardState.carrossel?.idcarrossel;
      if (!idcarrossel) return;

      const confirmed = await this.confirmIrreversibleBannerAction("remove");
      if (!confirmed) return;

      this.removingSlideId = slideId;

      try {
        const carrossel = await this.$store.dispatch(CARROSSEL_DELETE_SLIDE, {
          idcarrossel,
          idslide: slideId
        });
        this.wizardState.carrossel.slides = carrossel.slides || [];
        this.$emit("slide-deleted", slideId);
        this.$emit("dirty");
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Erro ao remover banner";
        this.$vueOnToast.pop("error", message);
      } finally {
        this.removingSlideId = null;
      }
    },
    captureStepBaseline() {
      this.stepBaseline = cloneWizardSnapshot(this.wizardState);
    },
    restoreStepBaseline() {
      if (!this.stepBaseline) return;
      restoreWizardFromSnapshot(this.wizardState, this.stepBaseline);
    },
    refreshSlideAction(updatedSlide) {
      const index = this.slides.findIndex(
        slide => slide.id === updatedSlide.slideId
      );
      if (index >= 0) {
        const slidesCopy = [...this.slides];
        slidesCopy[index] = {
          ...slidesCopy[index],
          action: updatedSlide.params
        };
        this.wizardState.carrossel.slides = slidesCopy;
        this.$emit("dirty");
      }
    },
    flushPendingActions() {
      this.$refs.slideActionPanel?.flushSlideActionSync?.();
    },
    validate() {
      this.flushPendingActions();
      const result = validateStepBanners(this.slides, {
        inicio: this.wizardState.carrossel?.inicio_vigencia,
        fim: this.wizardState.carrossel?.fim_vigencia
      });
      if (!result.valid) {
        this.$vueOnToast.pop("warning", result.message);
      }
      return result.valid;
    }
  }
};
</script>

<style scoped>
.step-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
  text-align: left;
}

.step-subtitle {
  color: #666;
  margin-bottom: 24px;
  text-align: left;
}

.step-column {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background: #fff;
  min-height: 280px;
}

.step-column--side {
  height: 100%;
}

.step-column--side.step-column--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
}

.banners-row {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 16px;
  padding-bottom: 8px;
}

.banner-card {
  flex: 0 0 200px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  background: #fff;
  position: relative;
}

.banner-card--selected {
  border-color: var(--primary-color, #5870ef);
}

.banner-card__handle {
  position: absolute;
  top: 8px;
  left: 8px;
  color: #999;
}

.banner-card__index {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
}

.banner-card__image {
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
  margin: 24px 0 12px;
}

.banner-card__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.banner-card--upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  cursor: default;
}

.upload-hint {
  margin-top: 8px;
  color: #888;
  text-align: center;
  font-size: 10px;
}

.side-panel-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
}

.side-panel-empty {
  color: #666;
  margin: 0;
  text-align: center;
}

::v-deep .banner-btn-replace.v-btn--outlined {
  border: thin solid var(--primary-color) !important;
  color: var(--primary-color) !important;
  background-color: transparent !important;
}

::v-deep .banner-btn-replace.v-btn--outlined .v-icon,
::v-deep .banner-btn-replace.v-btn--outlined span {
  color: var(--primary-color) !important;
}

::v-deep .banner-btn-remove.v-btn--outlined {
  border: thin solid var(--danger-color) !important;
  color: var(--danger-color) !important;
  background-color: transparent !important;
}

::v-deep .banner-btn-remove.v-btn--outlined .v-icon,
::v-deep .banner-btn-remove.v-btn--outlined span {
  color: var(--danger-color) !important;
}

.carrossel-step-banners--dark .step-title,
.carrossel-step-banners--dark .side-panel-title {
  color: rgba(255, 255, 255, 0.87);
}

.carrossel-step-banners--dark .step-subtitle,
.carrossel-step-banners--dark .side-panel-empty,
.carrossel-step-banners--dark .upload-hint {
  color: rgba(255, 255, 255, 0.65);
}

.carrossel-step-banners--dark .step-column {
  background: var(--dark-surface, #1d1d1d);
  border-color: rgba(255, 255, 255, 0.12);
}

.carrossel-step-banners--dark .banner-card {
  background: #2a2a2a;
  border-color: rgba(255, 255, 255, 0.12);
}

.carrossel-step-banners--dark .banner-card__handle {
  color: rgba(255, 255, 255, 0.5);
}

.hidden-input {
  display: none;
}
</style>
